import { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Users,
  Shield,
  Crown,
  Megaphone,
  DollarSign,
  Home,
  TrendingUp,
  Clock,
  CheckCircle,
  XCircle,
  Activity,
  FileText,
  Phone,
  Mail,
  MapPin,
  Calendar,
  Briefcase,
  Target,
  BarChart3,
  Loader2,
  AlertTriangle,
  ArrowUp,
  ArrowDown,
  Eye,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { User } from '../types/admin';
import { Deal } from '../types/deal';
import { dealAPI } from '../lib/deal-api';
import { logger } from '@/services/logger.service';

interface UserProfileTabProps {
  user: User;
  onBack: () => void;
}

const STAGE_LABELS: Record<string, string> = {
  'lead': 'Lead',
  'viewing-scheduled': 'Viewing Scheduled',
  'offer-made': 'Offer Made',
  'offer-accepted': 'Offer Accepted',
  'financing': 'Financing',
  'inspection': 'Inspection',
  'closing': 'Closing',
  'closed': 'Closed',
  'lost': 'Lost',
};

const STAGE_COLORS: Record<string, string> = {
  'lead': 'bg-slate-500',
  'viewing-scheduled': 'bg-blue-500',
  'offer-made': 'bg-indigo-500',
  'offer-accepted': 'bg-purple-500',
  'financing': 'bg-yellow-500',
  'inspection': 'bg-orange-500',
  'closing': 'bg-emerald-500',
  'closed': 'bg-green-600',
  'lost': 'bg-red-500',
};

const STAGE_BADGE_COLORS: Record<string, string> = {
  'lead': 'bg-slate-100 text-slate-800',
  'viewing-scheduled': 'bg-blue-100 text-blue-800',
  'offer-made': 'bg-indigo-100 text-indigo-800',
  'offer-accepted': 'bg-purple-100 text-purple-800',
  'financing': 'bg-yellow-100 text-yellow-800',
  'inspection': 'bg-orange-100 text-orange-800',
  'closing': 'bg-emerald-100 text-emerald-800',
  'closed': 'bg-green-100 text-green-800',
  'lost': 'bg-red-100 text-red-800',
};

const PIE_COLORS = ['#FDB022', '#3B82F6', '#8B5CF6', '#10B981', '#EF4444', '#F97316', '#6366F1', '#14B8A6', '#EC4899'];

export function UserProfileTab({ user, onBack }: UserProfileTabProps) {
  const [deals, setDeals] = useState<Deal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [profileTab, setProfileTab] = useState('overview');

  useEffect(() => {
    loadUserDeals();
  }, [user.id]);

  const loadUserDeals = async () => {
    setIsLoading(true);
    try {
      const allDeals = await dealAPI.getDeals();
      // Match deals by agent ID or by matching on user name (for cross-reference)
      const userDeals = allDeals.filter(
        (d) => d.agentId === user.id || d.agentName === user.name
      );
      setDeals(userDeals);
    } catch (error) {
      logger.error('Error loading deals for user', error as Error, undefined, 'UserProfileTab');
      setDeals([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleIcon = (role: string) => {
    switch (role) {
      case 'Super Admin': return <Crown className="w-5 h-5 text-yellow-500" />;
      case 'Admin': return <Shield className="w-5 h-5 text-blue-500" />;
      case 'Agent': return <Users className="w-5 h-5 text-green-500" />;
      case 'Marketing': return <Megaphone className="w-5 h-5 text-purple-500" />;
      default: return <Users className="w-5 h-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Active': return 'bg-green-100 text-green-800';
      case 'Inactive': return 'bg-gray-100 text-gray-800';
      case 'Suspended': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Derived data
  const activeDeals = deals.filter((d) => d.stage !== 'closed' && d.stage !== 'lost');
  const closedDeals = deals.filter((d) => d.stage === 'closed');
  const lostDeals = deals.filter((d) => d.stage === 'lost');
  const totalPipelineValue = activeDeals.reduce((sum, d) => sum + (d.offerAmount || 0), 0);
  const totalCommissionEarned = closedDeals.reduce((sum, d) => sum + (d.commission || 0), 0);
  const totalCommissionPending = activeDeals.reduce((sum, d) => sum + (d.commission || 0), 0);
  const avgProbability = activeDeals.length > 0
    ? Math.round(activeDeals.reduce((sum, d) => sum + d.probability, 0) / activeDeals.length)
    : 0;
  const winRate = deals.length > 0
    ? Math.round((closedDeals.length / (closedDeals.length + lostDeals.length || 1)) * 100)
    : 0;

  // Pipeline stage breakdown for chart
  const stageCounts = deals.reduce((acc, d) => {
    acc[d.stage] = (acc[d.stage] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const pipelineChartData = Object.entries(stageCounts).map(([stage, count]) => ({
    stage: STAGE_LABELS[stage] || stage,
    count,
  }));

  // Deal type breakdown
  const saleDeals = deals.filter((d) => d.type === 'sale');
  const rentalDeals = deals.filter((d) => d.type === 'rental');
  const dealTypeData = [
    { name: 'Sales', value: saleDeals.length },
    { name: 'Rentals', value: rentalDeals.length },
  ].filter((d) => d.value > 0);

  // Timeline data (deals by month)
  const dealsByMonth = deals.reduce((acc, d) => {
    const month = new Date(d.createdDate).toLocaleDateString('en-ZA', { month: 'short', year: '2-digit' });
    acc[month] = (acc[month] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const timelineData = Object.entries(dealsByMonth).map(([month, count]) => ({
    month,
    deals: count,
  }));

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-2" data-testid="profile-back">
          <ArrowLeft className="w-4 h-4" />
          Back to User Management
        </Button>
      </div>

      {/* User Profile Header */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row items-start md:items-center gap-6">
            <Avatar className="w-20 h-20">
              {(user.avatar || user.profilePicture) && (
                <AvatarImage src={user.avatar || user.profilePicture} alt={user.name} />
              )}
              <AvatarFallback className="text-2xl bg-[#FDB022]/20 text-[#FDB022]">
                {user.name.split(' ').map((n) => n[0]).join('')}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 space-y-2">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="text-2xl font-bold">{user.name}</h2>
                <Badge className={getStatusColor(user.status)}>{user.status}</Badge>
                <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
                  {getRoleIcon(user.role)}
                  <span>{user.role}</span>
                </div>
                {user.profilePicSource && (
                  <span className="text-[10px] text-muted-foreground/50" title="Profile picture source">
                    pic: {user.profilePicSource}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" />
                  {user.email}
                </span>
                {user.phone && (
                  <span className="flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5" />
                    {user.phone}
                  </span>
                )}
                <span className="flex items-center gap-1.5">
                  <MapPin className="w-3.5 h-3.5" />
                  {user.region}
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5" />
                  Joined {new Date(user.joinDate).toLocaleDateString('en-ZA', { month: 'long', year: 'numeric' })}
                </span>
              </div>
              {user.jobTitle && (
                <div className="flex items-center gap-1.5 text-sm mt-1">
                  <Briefcase className="w-3.5 h-3.5 text-[#FDB022]" />
                  <span className="text-muted-foreground">{user.jobTitle}</span>
                </div>
              )}
              {user.bio && (
                <p className="text-sm text-muted-foreground mt-2 max-w-2xl italic">&ldquo;{user.bio}&rdquo;</p>
              )}
              {user.phoneNumbers && user.phoneNumbers.length > 0 && (
                <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                  {user.phoneNumbers.map((num, i) => (
                    <span key={i} className="flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5" />
                      {num}
                    </span>
                  ))}
                </div>
              )}
              {user.department && (
                <div className="flex items-center gap-1.5 text-sm">
                  <Briefcase className="w-3.5 h-3.5 text-purple-500" />
                  <span>{user.department}</span>
                </div>
              )}
              {user.role === 'Agent' && user.commissionRate != null && (
                <div className="flex items-center gap-1.5 text-sm">
                  <DollarSign className="w-3.5 h-3.5 text-[#FDB022]" />
                  <span>Commission Rate: <strong>{user.commissionRate}%</strong></span>
                </div>
              )}
            </div>
            <div className="text-right text-sm text-muted-foreground">
              <p>Last login</p>
              <p className="font-medium text-foreground">
                {new Date(user.lastLogin).toLocaleDateString('en-ZA', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          <span className="ml-3 text-muted-foreground">Loading user data...</span>
        </div>
      ) : (
        <>
          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2 mb-1">
                  <Briefcase className="w-4 h-4 text-[#FDB022]" />
                  <span className="text-xs text-muted-foreground">Total Deals</span>
                </div>
                <p className="text-2xl font-bold">{deals.length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2 mb-1">
                  <Activity className="w-4 h-4 text-blue-500" />
                  <span className="text-xs text-muted-foreground">Active</span>
                </div>
                <p className="text-2xl font-bold">{activeDeals.length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-xs text-muted-foreground">Closed Won</span>
                </div>
                <p className="text-2xl font-bold">{closedDeals.length}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2 mb-1">
                  <Target className="w-4 h-4 text-purple-500" />
                  <span className="text-xs text-muted-foreground">Win Rate</span>
                </div>
                <p className="text-2xl font-bold">{winRate}%</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-4 h-4 text-green-600" />
                  <span className="text-xs text-muted-foreground">Commission Earned</span>
                </div>
                <p className="text-xl font-bold">R{totalCommissionEarned.toLocaleString()}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-[#FDB022]" />
                  <span className="text-xs text-muted-foreground">Pipeline Value</span>
                </div>
                <p className="text-xl font-bold">R{totalPipelineValue.toLocaleString()}</p>
              </CardContent>
            </Card>
          </div>

          {/* Profile sub-tabs */}
          <Tabs value={profileTab} onValueChange={setProfileTab}>
            <TabsList>
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="deals">
                All Deals ({deals.length})
              </TabsTrigger>
              <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
              <TabsTrigger value="financials">Financials</TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* User Details Card */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Profile Details</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-muted-foreground text-xs">Role</Label>
                        <p className="font-medium flex items-center gap-1.5 mt-0.5">
                          {getRoleIcon(user.role)}
                          {user.role}
                        </p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-xs">Region</Label>
                        <p className="font-medium mt-0.5">{user.region}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-xs">Properties Managed</Label>
                        <p className="font-medium mt-0.5">{user.propertiesManaged || 0}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-xs">Clients Managed</Label>
                        <p className="font-medium mt-0.5">{user.clientsManaged || 0}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-xs">Sales Completed</Label>
                        <p className="font-medium mt-0.5">{user.salesCompleted || 0}</p>
                      </div>
                      <div>
                        <Label className="text-muted-foreground text-xs">Commission (Profile)</Label>
                        <p className="font-medium mt-0.5">R{(user.commission || 0).toLocaleString()}</p>
                      </div>
                      {user.role === 'Agent' && user.commissionRate != null && (
                        <div>
                          <Label className="text-muted-foreground text-xs">Commission Rate</Label>
                          <p className="font-medium mt-0.5 text-[#FDB022]">{user.commissionRate}%</p>
                        </div>
                      )}
                    </div>
                    {user.role === 'Marketing' && (
                      <>
                        <Separator />
                        <div>
                          <Label className="text-muted-foreground text-xs">Department</Label>
                          <p className="font-medium flex items-center gap-1.5 mt-0.5">
                            <Megaphone className="w-3.5 h-3.5 text-purple-500" />
                            {user.department || 'Marketing'}
                          </p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground text-xs">Access</Label>
                          <p className="font-medium mt-0.5">Campaigns, Leads, Templates</p>
                        </div>
                      </>
                    )}
                    {user.role === 'Admin' && (
                      <>
                        <Separator />
                        <div>
                          <Label className="text-muted-foreground text-xs">Access Level</Label>
                          <p className="font-medium flex items-center gap-1.5 mt-0.5">
                            <Shield className="w-3.5 h-3.5 text-blue-500" />
                            System Management
                          </p>
                        </div>
                      </>
                    )}
                    {user.role === 'Super Admin' && (
                      <>
                        <Separator />
                        <div>
                          <Label className="text-muted-foreground text-xs">Access Level</Label>
                          <p className="font-medium flex items-center gap-1.5 mt-0.5">
                            <Crown className="w-3.5 h-3.5 text-yellow-500" />
                            Full Platform Oversight
                          </p>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>

                {/* Recent Deals */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Recent Deals</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {deals.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Briefcase className="w-10 h-10 mx-auto mb-2 opacity-40" />
                        <p className="text-sm">No deals found for this user</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {deals.slice(0, 5).map((deal) => (
                          <div key={deal.id} className="flex items-start justify-between p-3 rounded-lg border">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm truncate">{deal.propertyTitle}</p>
                              <p className="text-xs text-muted-foreground truncate">{deal.propertyAddress}</p>
                              <div className="flex items-center gap-2 mt-1.5">
                                <Badge className={`text-xs ${STAGE_BADGE_COLORS[deal.stage] || 'bg-gray-100 text-gray-800'}`}>
                                  {STAGE_LABELS[deal.stage] || deal.stage}
                                </Badge>
                                <Badge variant="outline" className="text-xs">
                                  {deal.type === 'sale' ? 'Sale' : 'Rental'}
                                </Badge>
                              </div>
                            </div>
                            <div className="text-right ml-3">
                              <p className="font-semibold text-sm">
                                R{(deal.offerAmount || 0).toLocaleString()}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {deal.probability}% prob.
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Charts Row */}
              {deals.length > 0 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Pipeline Breakdown */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Deal Stages</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ResponsiveContainer width="100%" height={250}>
                        <BarChart data={pipelineChartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                          <XAxis dataKey="stage" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={60} />
                          <YAxis allowDecimals={false} />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: 8 }}
                          />
                          <Bar dataKey="count" fill="#FDB022" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    </CardContent>
                  </Card>

                  {/* Deal Types Pie */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Deal Types</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex items-center justify-center">
                        <ResponsiveContainer width="100%" height={250}>
                          <PieChart>
                            <Pie
                              data={dealTypeData}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={90}
                              paddingAngle={5}
                              dataKey="value"
                              label={({ name, value }) => `${name}: ${value}`}
                            >
                              {dealTypeData.map((_, index) => (
                                <Cell key={`cell-${index}`} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip
                              contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: 8 }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                      <div className="flex justify-center gap-6 mt-2">
                        {dealTypeData.map((entry, index) => (
                          <div key={entry.name} className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full"
                              style={{ backgroundColor: PIE_COLORS[index % PIE_COLORS.length] }}
                            />
                            <span className="text-sm">{entry.name} ({entry.value})</span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}
            </TabsContent>

            {/* All Deals Tab */}
            <TabsContent value="deals" className="space-y-4">
              <Card>
                <CardContent className="pt-6">
                  {deals.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                      <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-40" />
                      <p>No deals found for this user</p>
                      <p className="text-sm mt-1">Deals will appear here once assigned</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Property</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Client</TableHead>
                          <TableHead>Stage</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Commission</TableHead>
                          <TableHead>Probability</TableHead>
                          <TableHead>Created</TableHead>
                          <TableHead>Expected Close</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {deals.map((deal) => (
                          <TableRow key={deal.id}>
                            <TableCell>
                              <div>
                                <p className="font-medium text-sm">{deal.propertyTitle}</p>
                                <p className="text-xs text-muted-foreground">{deal.propertyAddress}</p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {deal.type === 'sale' ? 'Sale' : 'Rental'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <p className="text-sm">{deal.clientName}</p>
                            </TableCell>
                            <TableCell>
                              <Badge className={`text-xs ${STAGE_BADGE_COLORS[deal.stage] || 'bg-gray-100 text-gray-800'}`}>
                                {STAGE_LABELS[deal.stage] || deal.stage}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium">
                              R{(deal.offerAmount || 0).toLocaleString()}
                            </TableCell>
                            <TableCell className="font-medium">
                              R{(deal.commission || 0).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                                  <div
                                    className="h-full rounded-full"
                                    style={{
                                      width: `${deal.probability}%`,
                                      backgroundColor:
                                        deal.probability >= 70 ? '#10B981' :
                                        deal.probability >= 40 ? '#FDB022' : '#EF4444',
                                    }}
                                  />
                                </div>
                                <span className="text-sm">{deal.probability}%</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">
                              {new Date(deal.createdDate).toLocaleDateString('en-ZA')}
                            </TableCell>
                            <TableCell className="text-sm">
                              {deal.expectedCloseDate
                                ? new Date(deal.expectedCloseDate).toLocaleDateString('en-ZA')
                                : '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Pipeline Tab */}
            <TabsContent value="pipeline" className="space-y-6">
              {/* Visual pipeline stages */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Deal Pipeline</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {Object.entries(STAGE_LABELS).map(([stageKey, stageLabel]) => {
                      const stageDeals = deals.filter((d) => d.stage === stageKey);
                      if (stageDeals.length === 0 && stageKey !== 'lead') return null;
                      const stageValue = stageDeals.reduce((sum, d) => sum + (d.offerAmount || 0), 0);
                      return (
                        <div key={stageKey} className="rounded-lg border p-4">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-3 h-3 rounded-full ${STAGE_COLORS[stageKey]}`} />
                              <span className="font-medium">{stageLabel}</span>
                              <Badge variant="outline" className="text-xs">
                                {stageDeals.length} deal{stageDeals.length !== 1 ? 's' : ''}
                              </Badge>
                            </div>
                            <span className="text-sm font-semibold">
                              R{stageValue.toLocaleString()}
                            </span>
                          </div>
                          {stageDeals.length > 0 && (
                            <div className="space-y-2 ml-6">
                              {stageDeals.map((deal) => (
                                <div
                                  key={deal.id}
                                  className="flex items-center justify-between p-2.5 rounded-md bg-muted/50"
                                >
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium truncate">{deal.propertyTitle}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {deal.clientName} &bull; {deal.type === 'sale' ? 'Sale' : 'Rental'}
                                    </p>
                                  </div>
                                  <div className="text-right ml-4">
                                    <p className="text-sm font-semibold">
                                      R{(deal.offerAmount || 0).toLocaleString()}
                                    </p>
                                    <p className="text-xs text-muted-foreground">{deal.probability}%</p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Average Probability */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Target className="w-4 h-4 text-blue-500" />
                      <span className="text-xs text-muted-foreground">Avg Probability (Active)</span>
                    </div>
                    <p className="text-2xl font-bold">{avgProbability}%</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <XCircle className="w-4 h-4 text-red-500" />
                      <span className="text-xs text-muted-foreground">Lost Deals</span>
                    </div>
                    <p className="text-2xl font-bold">{lostDeals.length}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-orange-500" />
                      <span className="text-xs text-muted-foreground">Pending Commission</span>
                    </div>
                    <p className="text-xl font-bold">R{totalCommissionPending.toLocaleString()}</p>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* Financials Tab */}
            <TabsContent value="financials" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className="w-4 h-4 text-green-600" />
                      <span className="text-xs text-muted-foreground">Total Commission Earned</span>
                    </div>
                    <p className="text-2xl font-bold">R{totalCommissionEarned.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-4 h-4 text-[#FDB022]" />
                      <span className="text-xs text-muted-foreground">Pending Commission</span>
                    </div>
                    <p className="text-2xl font-bold">R{totalCommissionPending.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <TrendingUp className="w-4 h-4 text-blue-500" />
                      <span className="text-xs text-muted-foreground">Pipeline Value</span>
                    </div>
                    <p className="text-2xl font-bold">R{totalPipelineValue.toLocaleString()}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-center gap-2 mb-1">
                      <BarChart3 className="w-4 h-4 text-purple-500" />
                      <span className="text-xs text-muted-foreground">Avg Deal Value</span>
                    </div>
                    <p className="text-2xl font-bold">
                      R{deals.length > 0
                        ? Math.round(
                            deals.filter(d => d.offerAmount > 0).reduce((sum, d) => sum + d.offerAmount, 0) /
                            (deals.filter(d => d.offerAmount > 0).length || 1)
                          ).toLocaleString()
                        : '0'}
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Financial Details Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Deal Financial Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  {deals.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <DollarSign className="w-10 h-10 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">No financial data available</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Property</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Stage</TableHead>
                          <TableHead>Offer Amount</TableHead>
                          <TableHead>Final Amount</TableHead>
                          <TableHead>Commission</TableHead>
                          <TableHead>Status</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {deals.map((deal) => (
                          <TableRow key={deal.id}>
                            <TableCell>
                              <p className="font-medium text-sm">{deal.propertyTitle}</p>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {deal.type === 'sale' ? 'Sale' : 'Rental'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className={`text-xs ${STAGE_BADGE_COLORS[deal.stage] || 'bg-gray-100 text-gray-800'}`}>
                                {STAGE_LABELS[deal.stage] || deal.stage}
                              </Badge>
                            </TableCell>
                            <TableCell className="font-medium">
                              R{(deal.offerAmount || 0).toLocaleString()}
                            </TableCell>
                            <TableCell className="font-medium">
                              {deal.finalAmount ? `R${deal.finalAmount.toLocaleString()}` : '—'}
                            </TableCell>
                            <TableCell className="font-medium">
                              R{(deal.commission || 0).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              {deal.stage === 'closed' ? (
                                <span className="flex items-center gap-1 text-green-600 text-sm">
                                  <CheckCircle className="w-3.5 h-3.5" /> Earned
                                </span>
                              ) : deal.stage === 'lost' ? (
                                <span className="flex items-center gap-1 text-red-500 text-sm">
                                  <XCircle className="w-3.5 h-3.5" /> Lost
                                </span>
                              ) : (
                                <span className="flex items-center gap-1 text-[#FDB022] text-sm">
                                  <Clock className="w-3.5 h-3.5" /> Pending
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                        {/* Totals Row */}
                        <TableRow className="font-semibold border-t-2">
                          <TableCell colSpan={3}>Totals</TableCell>
                          <TableCell>
                            R{deals.reduce((sum, d) => sum + (d.offerAmount || 0), 0).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            R{deals.filter(d => d.finalAmount).reduce((sum, d) => sum + (d.finalAmount || 0), 0).toLocaleString()}
                          </TableCell>
                          <TableCell>
                            R{deals.reduce((sum, d) => sum + (d.commission || 0), 0).toLocaleString()}
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>

              {/* Lost Deal Details */}
              {lostDeals.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-500" />
                      Lost Deals
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {lostDeals.map((deal) => (
                        <div key={deal.id} className="p-3 rounded-lg border border-red-900/30 bg-red-900/10">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium text-sm">{deal.propertyTitle}</p>
                              <p className="text-xs text-muted-foreground">{deal.clientName}</p>
                            </div>
                            <p className="text-sm font-medium">R{(deal.offerAmount || 0).toLocaleString()}</p>
                          </div>
                          {deal.lostReason && (
                            <p className="text-xs text-red-400 mt-2">
                              Reason: {deal.lostReason}
                            </p>
                          )}
                          {deal.notes && (
                            <p className="text-xs text-muted-foreground mt-1">{deal.notes}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </>
      )}
    </div>
  );
}